import { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserProfile, Expert } from '../../../types';
import { Loader2, Trash2, Sun, Square, CheckSquare, AlertTriangle, X } from 'lucide-react';

import { getCookie, setCookie } from '../../../utils/cookies';
import { formatDateForInput, todayInPoland } from '../../../utils/dateUtils';
import { addDaysYMD_Local, pickPublicationCalendarDay, randomLocalTimestampOnCalendarDay } from '../../../utils/horoscopePublicationDay';
import { loadCmsHoroscopeGeneratorSession, saveCmsHoroscopeGeneratorSession } from '../../../utils/cmsHoroscopeGeneratorSession';
import {
    cmsHoroscopeGeneratorFieldError,
    mergeHoroscopeAiPanelErrors
} from '../../../utils/cmsHoroscopeGeneratorValidators';
import AIGeneratorPanel from './AIGeneratorPanel';
import Pagination from '../../Pagination';

interface Horoscope {
    id: number;
    sign: string;
    vertical: string;
    period: string;
    period_start: string;
    period_end: string;
    text: string;
    published_at: string | null;
    created_at: string;
    updated_at: string;
}

interface MissingRecord {
    date: string;
    sign: string;
}

interface Props {
    horoscopeList: Horoscope[];
    totalCount: number;
    currentPage: number;
    onPageChange: (page: number) => void;
    limit: number | 'all';
    onLimitChange: (newLimit: number | 'all') => void;
    currentUser: UserProfile;
    experts: Expert[];
    onDataNeededRefresh: () => void;
}

interface LogEntry {
    id: number;
    timestamp: string;
    record: string;
    msg: string;
    details?: string;
}

const DEFAULT_DAILY_PROMPT =
    'Napisz horoskop dzienny dla znaku [ZODIAC_SIGN] na dzień [DATE_RANGE]. Horoskop powinien być w formacie HTML i podzielony na sekcje: ogólny, miłość, praca, zdrowie. Użyj nagłówków h2 z klasą "text-xl font-bold text-white mb-4" oraz paragrafów p z klasą "mb-4".';

const DailyTab: React.FC<Props> = ({ 
    horoscopeList, totalCount, currentPage, onPageChange, limit, onLimitChange, 
    currentUser, experts, onDataNeededRefresh 
}) => {
    const navigate = useNavigate();
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [loading, setLoading] = useState(false);

    // AI Generator State
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0 });
    const [generatorLogs, setGeneratorLogs] = useState<LogEntry[]>([]);
    const logIdCounter = useRef(0);
    const [promptModalContent, setPromptModalContent] = useState<string | null>(null);

    const [dailyGenSeed] = useState(() =>
        loadCmsHoroscopeGeneratorSession('daily', {
            promptTemplate: DEFAULT_DAILY_PROMPT,
            timeFrom: '00:01',
            timeTo: '06:00',
            pubDateFrom: addDaysYMD_Local(todayInPoland(), -180),
            pubDateTo: todayInPoland()
        })
    );

    const [pubDateFrom, setPubDateFrom] = useState(dailyGenSeed.pubDateFrom);
    const [pubDateTo, setPubDateTo] = useState(dailyGenSeed.pubDateTo);
    const [timeFrom, setTimeFrom] = useState(dailyGenSeed.timeFrom);
    const [timeTo, setTimeTo] = useState(dailyGenSeed.timeTo);

    const [promptTemplate, setPromptTemplate] = useState(dailyGenSeed.promptTemplate);
    const promptTextareaRef = useRef<HTMLTextAreaElement>(null);

    const [selectedExpertIds, setSelectedExpertIds] = useState<Set<number>>(
        () => new Set(dailyGenSeed.selectedExpertIds)
    );
    
    const [rangeError, setRangeError] = useState<string | null>(null);

    const [startDate, setStartDate] = useState(() => {
        const saved = getCookie('horoscopeDailyStartDate');
        if (saved) return saved;
        const today = new Date();
        const sevenDaysAgo = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
        return formatDateForInput(sevenDaysAgo);
    });

    const [endDate, setEndDate] = useState(() => {
        const saved = getCookie('horoscopeDailyEndDate');
        if (saved) return saved;
        const today = new Date();
        const sevenDaysHence = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 7);
        return formatDateForInput(sevenDaysHence);
    });

    const [localStartDate, setLocalStartDate] = useState(startDate);
    const [localEndDate, setLocalEndDate] = useState(endDate);

    const [missingRecords, setMissingRecords] = useState<MissingRecord[]>([]);
    const [loadingMissing, setLoadingMissing] = useState(false);

    useEffect(() => { setLocalStartDate(startDate); }, [startDate]);
    useEffect(() => { setLocalEndDate(endDate); }, [endDate]);

    const handleDateChangeCommit = () => {
        if (localStartDate !== startDate) setStartDate(localStartDate);
        if (localEndDate !== endDate) setEndDate(localEndDate);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleDateChangeCommit();
            (e.target as HTMLInputElement).blur();
        }
    };
    
    useEffect(() => { setCookie('horoscopeDailyStartDate', startDate, 365); }, [startDate]);
    useEffect(() => { setCookie('horoscopeDailyEndDate', endDate, 365); }, [endDate]);

    useEffect(() => {
        saveCmsHoroscopeGeneratorSession('daily', {
            pubDateFrom,
            pubDateTo,
            timeFrom,
            timeTo,
            promptTemplate,
            selectedExpertIds: Array.from(selectedExpertIds)
        });
    }, [pubDateFrom, pubDateTo, timeFrom, timeTo, promptTemplate, selectedExpertIds]);

    const dailyHoroscopes = useMemo(() => {
        // This list is now paginated from the server
        return horoscopeList;
    }, [horoscopeList]);
    
    useEffect(() => {
        try {
            const start = new Date(startDate);
            const end = new Date(endDate);
            if (start > end) {
                setRangeError("Start date cannot be after end date.");
                return;
            }
            const diffTime = Math.abs(end.getTime() - start.getTime());
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            const MAX_DAY_RANGE = 90; // Approx 3 months
            if (diffDays > MAX_DAY_RANGE) {
                setRangeError(`Zakres dat jest zbyt duży (maksymalnie ${MAX_DAY_RANGE} dni).`);
            } else {
                setRangeError(null);
            }
        } catch (_e) {
            setRangeError("Invalid date format.");
        }
    }, [startDate, endDate]);

    const generatorFieldsError = useMemo(
        () => cmsHoroscopeGeneratorFieldError(pubDateFrom, pubDateTo, timeFrom, timeTo),
        [pubDateFrom, pubDateTo, timeFrom, timeTo]
    );

    const aiPanelBlockingError = useMemo(
        () => mergeHoroscopeAiPanelErrors(rangeError, generatorFieldsError),
        [rangeError, generatorFieldsError]
    );
    
    // Fetch missing records from the new API endpoint
    useEffect(() => {
        const fetchMissing = async () => {
            if (rangeError || !startDate || !endDate) {
                setMissingRecords([]);
                return;
            };
            setLoadingMissing(true);
            try {
                const url = `/api/cms/horoscope?check_missing=true&period=daily&start_date=${startDate}&end_date=${endDate}`;
                const res = await fetch(url, { headers: { 'x-admin-phone': currentUser.phone } });
                if (res.ok) {
                    setMissingRecords(await res.json());
                } else {
                    console.error("Failed to fetch missing records");
                    setMissingRecords([]);
                }
            } catch (_e) {
                console.error(_e);
            } finally {
                setLoadingMissing(false);
            }
        };
        fetchMissing();
    }, [startDate, endDate, rangeError, currentUser.phone]);


    const handleSelectRow = (id: number) => {
        setSelectedIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) newSet.delete(id); else newSet.add(id);
            return newSet;
        });
    };
    
    const handleSelectAllVisible = () => {
        if (selectedIds.size === dailyHoroscopes.length) setSelectedIds(new Set());
        else setSelectedIds(new Set(dailyHoroscopes.map(item => item.id)));
    };
    
    const handleBulkDelete = async () => {
        if (selectedIds.size === 0 || !confirm(`Delete ${selectedIds.size} entries?`)) return;
        setLoading(true);
        try {
            const res = await fetch('/api/cms/horoscope', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                body: JSON.stringify({ ids: Array.from(selectedIds) })
            });
            if (res.ok) {
                setSelectedIds(new Set());
                onDataNeededRefresh();
            } else alert('Delete failed.');
        } catch (_e) { console.error(_e); }
        setLoading(false);
    };

    const formatDateLocale = (dateString: string | null) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('pl-PL');
    }
    
    const handleSelectExpert = (id: number) => {
        setSelectedExpertIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) newSet.delete(id); else newSet.add(id);
            return newSet;
        });
    };

    const handleSelectAllExperts = (select: boolean) => {
        if (select) setSelectedExpertIds(new Set(experts.filter(e=>e.user_id).map(e => e.user_id!)));
        else setSelectedExpertIds(new Set());
    };

    const handleGenerate = async () => {
        const recordsToGenerate = missingRecords;
        if (recordsToGenerate.length === 0) return;

        setIsGenerating(true);
        setGenerationProgress({ current: 0, total: recordsToGenerate.length });
        setGeneratorLogs([]);
        logIdCounter.current = 0;

        for (const record of recordsToGenerate) {
            setGenerationProgress(prev => ({ ...prev, current: prev.current + 1 }));
            const log = (msg: string, details?: string) => {
                setGeneratorLogs(prev => [{ id: logIdCounter.current++, timestamp: new Date().toLocaleTimeString(), record: `${record.date} - ${record.sign}`, msg, details }, ...prev]);
            };

            log('Starting...');

            const expertIdArray = Array.from(selectedExpertIds);
            let author_id_json = null;
            let randomExpert: Expert | undefined = undefined;

            if (expertIdArray.length > 0) {
                const randomExpertId = expertIdArray[Math.floor(Math.random() * expertIdArray.length)];
                author_id_json = JSON.stringify([String(randomExpertId)]);
                randomExpert = experts.find(e => e.user_id === randomExpertId);
                log(`Assigning author: ${randomExpert?.name || `ID ${randomExpertId}`}`);
            }
            
            const dateRangeStr = new Date(record.date).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' });
            
            const finalPrompt = promptTemplate
                .replace(/\[ZODIAC_SIGN\]/g, record.sign)
                .replace(/\[PERIOD\]/g, 'dzienny')
                .replace(/\[DATE_RANGE\]/g, dateRangeStr)
                .replace(/\[EXPERT_NAME\]/g, randomExpert ? randomExpert.name : '')
                .replace(/\[EXPERT_STYLE\]/g, randomExpert && randomExpert.style ? randomExpert.style : '');

            log('Generating content from AI...', finalPrompt);
            let generatedText = '';
            try {
                const genRes = await fetch('/api/cms/generate', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ type: 'horoscope_yearly', prompt: finalPrompt })
                });
                if (!genRes.ok) throw new Error(`AI API Error: ${(await genRes.json()).error}`);
                const { text } = await genRes.json();
                generatedText = text;
                log('Content received.');
            } catch (e: unknown) {
                if (e instanceof Error) {
                    log(`Error: ${e.message}`);
                } else {
                    log(`Error: ${String(e)}`);
                }
                continue;
            }
            
            if (!generatedText) {
                log('Error: AI returned empty content. Skipping save.');
                continue;
            }

            const { dayYmd, usedIntersection } = pickPublicationCalendarDay({
                logicalPublicationStart: record.date,
                logicalPublicationEnd: record.date,
                pubDateFrom,
                pubDateTo
            });
            if (!usedIntersection) {
                log('Uwaga: brak przecięcia zakresu publikacji z dniem treści (użyto daty granicznej wg limitów).');
            }
            const randomDate = randomLocalTimestampOnCalendarDay(dayYmd, timeFrom, timeTo);
            const randomDateISO = randomDate.toISOString();

            log('Saving to database...');
            try {
                const saveRes = await fetch('/api/cms/horoscope', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        sign: record.sign,
                        vertical: '-',
                        period: 'daily',
                        period_start: record.date,
                        period_end: record.date,
                        text: generatedText,
                        published_at: randomDateISO,
                        created_at: randomDateISO,
                        updated_at: randomDateISO,
                        author_id: author_id_json
                    })
                });
                if (!saveRes.ok) throw new Error(`DB Save Error: ${(await saveRes.json()).error}`);
                log('Saved successfully!');
            } catch (e: unknown) {
                if (e instanceof Error) {
                    log(`Error: ${e.message}`);
                } else {
                    log(`Error: ${String(e)}`);
                }
            }
        }
        
        setIsGenerating(false);
        onDataNeededRefresh();
    };

    const columns = [
        { key: 'id', label: 'ID', width: '50px' },
        { key: 'sign', label: 'Sign' },
        { key: 'period', label: 'Period' },
        { key: 'period_start', label: 'Date' },
        { key: 'text', label: 'Text', width: '300px' },
        { key: 'published_at', label: 'Published' }
    ];

    return (
        <div className="grid grid-cols-10 gap-6">
            <div className="col-span-7 space-y-6">
                <section>
                    <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-yellow-400" /> Missing Records ({rangeError ? 0 : missingRecords.length} total)
                    </h2>
                    <div className="bg-mystic-box border border-gray-800 p-4 rounded-lg">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="text-xs text-gray-400 font-bold">Date Range:</div>
                            <input 
                                type="date" 
                                value={localStartDate} 
                                onChange={e => setLocalStartDate(e.target.value)}
                                onBlur={handleDateChangeCommit} onKeyDown={handleKeyDown}
                                className="bg-mystic-inset border border-gray-700 p-1 rounded text-white text-xs" />
                            <span className="text-gray-500">-</span>
                            <input 
                                type="date" 
                                value={localEndDate} 
                                onChange={e => setLocalEndDate(e.target.value)}
                                onBlur={handleDateChangeCommit} onKeyDown={handleKeyDown}
                                className="bg-mystic-inset border border-gray-700 p-1 rounded text-white text-xs" />
                        </div>
                        {rangeError && <div className="p-2 bg-red-900/20 border border-red-800 text-red-300 text-xs rounded mb-2">{rangeError}</div>}
                        <div className="max-h-60 overflow-y-auto custom-scrollbar border-t border-gray-800 pt-2">
                            {loadingMissing ? <div className="py-4 text-center text-gray-500"><Loader2 className="animate-spin w-6 h-6 mx-auto"/></div> : (
                                <table className="w-full text-left text-xs">
                                    <thead className="text-gray-500 font-bold uppercase text-[10px]"><tr><th className="py-1">Date</th><th className="py-1">Sign</th></tr></thead>
                                    <tbody className="divide-y divide-gray-800">
                                        {!rangeError && missingRecords.map((record, index) => (
                                            <tr key={index} className="text-gray-300 hover:bg-white/5">
                                                <td className="py-1.5 font-mono">{record.date}</td>
                                                <td className="py-1.5">{record.sign}</td>
                                            </tr>
                                        ))}
                                        {!rangeError && missingRecords.length === 0 && (<tr><td colSpan={2} className="py-4 text-center text-gray-500 italic">No missing records found.</td></tr>)}
                                        {rangeError && (<tr><td colSpan={2} className="py-4 text-center text-red-400 italic">Calculation paused.</td></tr>)}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </section>
                
                <section>
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-bold text-white flex items-center gap-2"><Sun className="w-5 h-5" /> Daily Records ({totalCount})</h2>
                        {selectedIds.size > 0 && (<button onClick={handleBulkDelete} disabled={loading} className="flex items-center gap-1 bg-red-900/40 text-red-400 border border-red-800 hover:bg-red-900/60 px-3 py-1 rounded text-xs font-bold uppercase">{loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Trash2 className="w-4 h-4" />} Delete ({selectedIds.size})</button>)}
                    </div>

                    <div className="overflow-x-auto border border-gray-800 rounded-sm">
                        <table className="w-full text-left border-collapse table-fixed">
                            <thead className="bg-mystic-box">
                                <tr className="border-b border-gray-800 text-gray-500 text-[10px] uppercase font-bold tracking-wider">
                                    <th className="p-2 border-r border-gray-800 w-10 text-center sticky left-0 bg-mystic-box"><div onClick={handleSelectAllVisible} className="cursor-pointer flex justify-center">{dailyHoroscopes.length > 0 && selectedIds.size === dailyHoroscopes.length ? <CheckSquare className="w-4 h-4 text-blue-400" /> : <Square className="w-4 h-4" />}</div></th>
                                    {columns.map(col => (<th key={col.key} className="p-2 border-r border-gray-800 last:border-r-0" style={{ minWidth: col.width || '120px' }}>{col.label}</th>))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800 bg-mystic-bg">
                                {dailyHoroscopes.map(item => (
                                    <tr key={item.id} className={`group hover:bg-mystic-btn ${selectedIds.has(item.id) ? 'bg-blue-900/10' : ''}`}>
                                        <td className="p-2 border-r border-gray-800 text-center sticky left-0 bg-mystic-bg group-hover:bg-mystic-btn"><div onClick={() => handleSelectRow(item.id)} className="cursor-pointer flex justify-center">{selectedIds.has(item.id) ? <CheckSquare className="w-4 h-4 text-blue-400" /> : <Square className="w-4 h-4 text-gray-700 group-hover:text-gray-500" />}</div></td>
                                        {columns.map(col => {
                                            const value = item[col.key as keyof Horoscope];
                                            let displayValue: React.ReactNode = typeof value === 'string' ? value : value;
                                            if (col.key === 'id') displayValue = <button onClick={() => navigate(`/cms/horoscope/edit/${item.id}`)} className="font-bold text-mystic-accent hover:underline text-left">{item.id}</button>;
                                            else if (col.key.endsWith('_at')) displayValue = formatDateLocale(value as string | null);
                                            else if (col.key === 'text' && typeof value === 'string') displayValue = <span className="text-gray-500 italic truncate block max-w-xs">{value}</span>;
                                            return <td key={col.key} className="p-2 border-r border-gray-800 last:border-r-0 text-xs text-gray-300">{displayValue}</td>;
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                     {totalCount > (typeof limit === 'number' ? limit : 0) && (
                        <div className="mt-4">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={Math.ceil(totalCount / (typeof limit === 'number' ? limit : 50))}
                                onPageChange={onPageChange}
                            />
                        </div>
                    )}
                </section>
            </div>
            
            <AIGeneratorPanel
                isGenerating={isGenerating}
                generationProgress={generationProgress}
                experts={experts}
                selectedExpertIds={selectedExpertIds}
                onSelectExpert={handleSelectExpert}
                onSelectAllExperts={handleSelectAllExperts}
                pubDateFrom={pubDateFrom}
                onPubDateFromChange={setPubDateFrom}
                pubDateTo={pubDateTo}
                onPubDateToChange={setPubDateTo}
                timeFrom={timeFrom}
                onTimeFromChange={setTimeFrom}
                timeTo={timeTo}
                onTimeToChange={setTimeTo}
                promptTemplate={promptTemplate}
                onPromptTemplateChange={setPromptTemplate}
                promptTextareaRef={promptTextareaRef}
                placeholders={['[ZODIAC_SIGN]', '[PERIOD]', '[DATE_RANGE]', '[EXPERT_NAME]', '[EXPERT_STYLE]']}
                onGenerate={handleGenerate}
                missingRecordsCount={missingRecords.length}
                generatorLogs={generatorLogs}
                setPromptModalContent={setPromptModalContent}
                rangeError={aiPanelBlockingError}
            />

            {promptModalContent && (
                <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPromptModalContent(null)}>
                    <div className="bg-mystic-box border border-gray-700 w-full max-w-3xl max-h-[90vh] rounded-xl flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
                        <div className="p-4 border-b border-gray-800 flex justify-between items-center"><h3 className="font-bold text-white">Full AI Prompt</h3><button onClick={() => setPromptModalContent(null)} className="p-1 text-gray-500 hover:text-white"><X /></button></div>
                        <div className="p-6 flex-1 overflow-auto"><pre className="text-sm text-gray-300 whitespace-pre-wrap font-sans">{promptModalContent}</pre></div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DailyTab;
